import React, { useEffect, useRef } from "react";

interface Particle {
  x: number;
  y: number;
  radius: number;
  vx: number;
  vy: number;
  baseAlpha: number;
  pulseSpeed: number;
  pulseOffset: number;
  color: string;
  hasGlow: boolean;
  type: "gold" | "white";
}

const GOLD_PALETTE = [
  "#FFD700", // Bright Pure Gold
  "#FBBF24", // Warm Amber Gold
  "#F59E0B", // Deep Golden Ember
  "#FEF08A", // Pale Radiant Gold
  "#FCD34D", // Soft Sunlit Gold
];

const WHITE_PALETTE = [
  "#FFFFFF", // Crisp Pure White
  "#F8FAFC", // Starlight Diamond White
  "#F1F5F9", // Luminous Silver White
  "#E2E8F0", // Soft Pearl White
  "#F3F4F6", // Shimmer Mist White
];

export const GoldenParticlesBackground: React.FC<{ className?: string }> = ({ className = "" }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationFrameId: number;
    let width = (canvas.width = canvas.parentElement?.clientWidth || window.innerWidth);
    let height = (canvas.height = canvas.parentElement?.clientHeight || window.innerHeight);

    // Dynamic density based on screen dimensions
    const particleCount = Math.min(150, Math.max(80, Math.floor((width * height) / 14000)));
    const particles: Particle[] = [];

    for (let i = 0; i < particleCount; i++) {
      const radius = Math.random() * 1.6 + 0.8; // 0.8px to 2.4px
      const isWhite = i % 2 === 1; // Elegant 50/50 distribution of golden and white particles
      const palette = isWhite ? WHITE_PALETTE : GOLD_PALETTE;

      particles.push({
        x: Math.random() * width,
        y: Math.random() * height,
        radius,
        // Smooth gentle floating drift
        vx: (Math.random() - 0.5) * 0.35,
        vy: -Math.random() * 0.4 - 0.1, // Soft upward floating
        baseAlpha: Math.random() * 0.5 + 0.35,
        pulseSpeed: Math.random() * 0.025 + 0.015,
        pulseOffset: Math.random() * Math.PI * 2,
        color: palette[Math.floor(Math.random() * palette.length)],
        hasGlow: radius > 1.3,
        type: isWhite ? "white" : "gold",
      });
    }

    let time = 0;

    const render = () => {
      time += 1;

      // Dark black canvas background
      ctx.fillStyle = "#000000";
      ctx.fillRect(0, 0, width, height);

      // Subtle ambient gold radial sheen in the center for depth (very soft, high-luxury)
      const centerGlow = ctx.createRadialGradient(
        width / 2,
        height * 0.38,
        50,
        width / 2,
        height * 0.38,
        width * 0.65
      );
      centerGlow.addColorStop(0, "rgba(245, 158, 11, 0.035)");
      centerGlow.addColorStop(0.5, "rgba(217, 119, 6, 0.015)");
      centerGlow.addColorStop(1, "rgba(0, 0, 0, 0)");
      ctx.fillStyle = centerGlow;
      ctx.fillRect(0, 0, width, height);

      // Render & update each particle (golden and white)
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];

        // Organic twinkling / pulsing
        const alphaPulse = Math.sin(time * p.pulseSpeed + p.pulseOffset);
        const currentAlpha = Math.max(0.1, Math.min(1, p.baseAlpha + alphaPulse * 0.28));

        // Slight horizontal wave motion for natural fluid float
        p.x += p.vx + Math.sin(time * 0.015 + p.pulseOffset) * 0.18;
        p.y += p.vy;

        // Wrap around screen boundaries with margin
        if (p.y < -15) {
          p.y = height + 10;
          p.x = Math.random() * width;
        } else if (p.y > height + 15) {
          p.y = -10;
          p.x = Math.random() * width;
        }

        if (p.x < -15) {
          p.x = width + 10;
        } else if (p.x > width + 15) {
          p.x = -10;
        }

        // Draw soft glow for luminous particles (with matched color gradient)
        if (p.hasGlow) {
          const glowGrad = ctx.createRadialGradient(
            p.x,
            p.y,
            0,
            p.x,
            p.y,
            p.radius * 4
          );

          if (p.type === "white") {
            glowGrad.addColorStop(0, `rgba(255, 255, 255, ${currentAlpha * 0.55})`);
            glowGrad.addColorStop(0.5, `rgba(226, 232, 240, ${currentAlpha * 0.22})`);
            glowGrad.addColorStop(1, "rgba(0, 0, 0, 0)");
          } else {
            glowGrad.addColorStop(0, `rgba(255, 215, 0, ${currentAlpha * 0.5})`);
            glowGrad.addColorStop(0.5, `rgba(245, 158, 11, ${currentAlpha * 0.2})`);
            glowGrad.addColorStop(1, "rgba(0, 0, 0, 0)");
          }

          ctx.beginPath();
          ctx.arc(p.x, p.y, p.radius * 4, 0, Math.PI * 2);
          ctx.fillStyle = glowGrad;
          ctx.fill();
        }

        // Draw core particle
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = currentAlpha;
        ctx.fill();
        ctx.globalAlpha = 1.0;
      }

      animationFrameId = requestAnimationFrame(render);
    };

    render();

    // Handle resize dynamically
    const handleResize = () => {
      if (!canvas) return;
      width = canvas.width = canvas.parentElement?.clientWidth || window.innerWidth;
      height = canvas.height = canvas.parentElement?.clientHeight || window.innerHeight;
    };

    const resizeObserver = new ResizeObserver(() => handleResize());
    if (canvas.parentElement) {
      resizeObserver.observe(canvas.parentElement);
    }
    window.addEventListener("resize", handleResize);

    return () => {
      cancelAnimationFrame(animationFrameId);
      resizeObserver.disconnect();
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <div
      id="golden-particles-bg-container"
      className={`absolute inset-0 z-0 bg-black overflow-hidden pointer-events-none select-none ${className}`}
    >
      <canvas
        id="golden-particles-canvas"
        ref={canvasRef}
        className="w-full h-full block"
      />
    </div>
  );
};
